// app/src/lib/backup/sharedSnapshot.js — owner→viewers read-only data sharing.
//
// Model: ONE owner, ONE device — your phone, and that's it. The owner's device
// is always the writer. After any change to the books it (debounced) uploads a
// gzipped (NOT client-encrypted) snapshot to
// s3://{backups}/{accountId}/books-shared.json.gz. That blob serves two readers:
//   - team VIEWERS, who mirror it read-only into their local Dexie; and
//   - the owner on a FRESH install / new phone, who rehydrates from it once
//     (recovery) when local is empty.
// There is no multi-device writer coordination: the cloud blob is downstream of
// local, never the reverse, so a pull can never overwrite the owner's local
// books. SSE at rest + a membership-gated presigned URL is the access boundary.

import pako from 'pako';
import { db } from '../db.js';
import { deriveKeys, encrypt, decrypt } from '../crypto.js';
import * as kv from '../idbKv.js';
import { getAccessToken } from '../auth/cognitoClient.js';
import { currentAccount } from '../scale/account.js';
import { getConfig } from '../config.js';

// We share the owner's BOOKS, not their account/membership/scale machinery.
// Rather than hand-maintain an allow-list (which silently fell behind every new
// tile — supplies, recipes, poLineItems, maintenanceProviders, … were all
// missing, so viewers never saw them), we derive the shared set from the LIVE
// Dexie schema MINUS this explicit deny-list. New tiles then sync to viewers
// automatically — you can't forget to add one.
//
// Excluded here, and why each must NOT be copied onto a viewer:
//   - membership / billing / scale machinery: viewer-specific or per-account.
//     The owner's rows must never overwrite the viewer's own (planState carries
//     the viewer's tier/trial; members/invitations/auditEntries/approvalRequests/
//     integrations/webhook* are the account's own control plane).
//   - local-only bookkeeping (appliedWalCheckpoints) and large device-local
//     blobs (receiptImages) — pointless/expensive to ship in the snapshot.
// Every shared table carries a `userId` field that loadSnapshot() re-stamps.
const NON_SHARED_TABLES = new Set([
  'planState', 'members', 'invitations', 'auditEntries', 'approvalRequests',
  'integrations', 'webhookSubscriptions', 'webhookDeliveries',
  'appliedWalCheckpoints', 'receiptImages',
]);

/** The book tables to share: every Dexie store except the non-book set above. */
function sharedTables() {
  return db.tables.map((t) => t.name).filter((n) => !NON_SHARED_TABLES.has(n));
}

// Tables we probe to decide whether THIS device has any books yet. If all are
// empty for the current user, a fresh sign-in / new device should rehydrate
// from the cloud blob (recovery) rather than upload an empty snapshot.
const PRESENCE_TABLES = ['invoices', 'plaidTransactions', 'salesEntries', 'vendors', 'items'];

/** True when the local Dexie holds no books for `userId` (new device / post sign-out). */
async function localBooksEmpty(userId) {
  for (const t of PRESENCE_TABLES) {
    try {
      const n = await db.table(t).where('userId').equals(userId).count();
      if (n > 0) return false;
    } catch { /* table may not exist on older schema — ignore */ }
  }
  return true;
}

// ── Optional client-side encryption of the shared blob (owner decision
// 2026-07-11): SOLO books encrypt end-to-end — the service cannot read them;
// SHARING requires a service-readable blob (viewers must decrypt it and hold
// no owner key), so team accounts stay gzip+SSE, with explicit consent copy in
// the invite flow. Encryption is ACTIVE only when the owner set a recovery
// phrase (encryptBooks flag — the phrase escrows the device salt, so a new
// phone stays recoverable) AND no team members exist. The two wire formats are
// self-distinguishing: gzip magic (0x1f 0x8b) vs our AES-GCM envelope (0x01),
// so mixed states and old blobs decode without a flag day.

const gzMagic = (buf) => buf && buf.length > 2 && buf[0] === 0x1f && buf[1] === 0x8b;
const sharedAad = (accountId) => new TextEncoder().encode(`kanjo/books-shared/v1|${accountId}`);

// Team membership lives SERVER-side (scale/team.js /team API) — the local
// db.members table is the legacy path. The solo check consults the server with
// a short cache, persists the last-known count to userProfile for offline
// boots, and treats never-known as solo. Local db.members rows still count as
// members (belt and suspenders for the legacy flow).
let _teamCache = { at: 0, count: null };
async function teamMemberCount(acct) {
  let server = null;
  if (_teamCache.count != null && Date.now() - _teamCache.at < 10 * 60_000) {
    server = _teamCache.count;
  } else {
    try {
      const { summary } = await import('../scale/team.js');
      const s = await summary();
      server = (s?.members?.length || 0) + (s?.invites?.length || 0);
      _teamCache = { at: Date.now(), count: server };
      const prof = (await db.userProfile.get(acct.sub)) || { userId: acct.sub };
      if (prof.teamMemberCount !== server) await db.userProfile.put({ ...prof, userId: acct.sub, teamMemberCount: server });
    } catch {
      const prof = await db.userProfile.get(acct.sub).catch(() => null);
      server = typeof prof?.teamMemberCount === 'number' ? prof.teamMemberCount : 0;
    }
  }
  let local = 0;
  try {
    local = await db.members
      .where('[ownerId+status]').anyOf([[acct.sub, 'active'], [acct.sub, 'pending']]).count();
  } catch { /* legacy table absent */ }
  return server + local;
}

/** Force the next teamMemberCount to re-ask the server (call after invite/remove). */
export function invalidateTeamCache() { _teamCache = { at: 0, count: null }; }

/** Is client-side encryption in force for this owner right now? */
export async function booksEncryptionActive(acct) {
  if (!acct || acct.role !== 'owner') return false;
  try {
    const prof = await db.userProfile.get(acct.sub);
    if (!prof || prof.encryptBooks !== true) return false;
    return (await teamMemberCount(acct)) === 0; // any member (even a pending invite) → plaintext
  } catch { return false; }
}

/** gzip (+ optionally encrypt) a snapshot object. Pure given a key — testable. */
export async function encodeSharedBlob(snapshot, { key = null, accountId = '' } = {}) {
  const gz = pako.gzip(new TextEncoder().encode(JSON.stringify(snapshot)));
  if (!key) return { buf: gz, encrypted: false };
  return { buf: await encrypt(key, gz, sharedAad(accountId)), encrypted: true };
}

/** Decode either wire format back to the snapshot object. Throws
 *  code='ENCRYPTED_NEEDS_PHRASE' when the blob is encrypted and this device's
 *  key can't open it (new device before phrase recovery — or a viewer, who by
 *  design never can). */
export async function decodeSharedBlob(buf, { key = null, accountId = '' } = {}) {
  if (gzMagic(buf)) return JSON.parse(new TextDecoder().decode(pako.ungzip(buf)));
  try {
    if (!key) throw new Error('no key');
    const gz = await decrypt(key, buf, sharedAad(accountId));
    return JSON.parse(new TextDecoder().decode(pako.ungzip(gz)));
  } catch {
    const err = new Error('shared-snapshot: encrypted — this device needs the recovery phrase');
    err.code = 'ENCRYPTED_NEEDS_PHRASE';
    throw err;
  }
}

const DEBOUNCE_MS = 5000;
let _dirty = false;
let _timer = null;
let _inflight = false;
let _loading = false;       // true while loadSnapshot() writes — suppresses the dirty hook
let _hooksInstalled = false;

/** Request a PUT presign for the shared blob → { url }. */
async function presignSharedPut() {
  const cfg = getConfig();
  const at = await getAccessToken();
  if (!at) throw new Error('shared-snapshot: no access token');
  const res = await fetch(`${cfg.apiBaseUrl}/backup/presign`, {
    method: 'POST',
    headers: { authorization: `Bearer ${at}`, 'content-type': 'application/json' },
    body: JSON.stringify({ kind: 'shared' }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(`shared-snapshot: presign ${res.status}`); e.status = res.status; throw e; }
  if (!body.url) throw new Error('shared-snapshot: malformed presign');
  return body; // { url }
}

/** Request a GET presign for the shared blob → { url }. */
async function presignSharedGet() {
  const cfg = getConfig();
  const at = await getAccessToken();
  if (!at) throw new Error('shared-snapshot: no access token');
  const res = await fetch(`${cfg.apiBaseUrl}/backup/presign?op=get&scope=shared`, {
    headers: { authorization: `Bearer ${at}` },
  });
  if (!res.ok) { const e = new Error(`shared-snapshot: presign ${res.status}`); e.status = res.status; throw e; }
  const body = await res.json();
  if (!body.url) throw new Error('shared-snapshot: malformed presign');
  return body; // { url }
}

/**
 * OWNER only: build + upload the shared books snapshot. No-op for viewers (who
 * are read-only). The blob is a plain last-write-wins mirror of local — there is
 * no device coordination, so this just writes whatever local holds right now.
 */
export async function shareSnapshotNow() {
  const acct = await currentAccount();
  if (!acct || acct.role === 'viewer') return { skipped: true };
  const snapshot = { version: 1, exportedAt: Date.now(), accountId: acct.accountId, tables: {} };
  for (const t of sharedTables()) snapshot.tables[t] = await db.table(t).toArray();

  // Solo + phrase set → encrypt. An encryption FAILURE aborts the upload (loud,
  // previous blob stays): silently falling back to plaintext would betray the
  // "we cannot read your books" promise the toggle made.
  let key = null;
  if (await booksEncryptionActive(acct)) {
    key = (await deriveKeys(acct.sub, kv)).backupKey;
  }
  const { buf, encrypted } = await encodeSharedBlob(snapshot, { key, accountId: acct.accountId });

  const { url } = await presignSharedPut();
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'content-type': 'application/octet-stream' },
    body: buf,
  });
  if (!res.ok) throw new Error(`shared-snapshot: PUT ${res.status}`);
  _dirty = false;
  return { bytes: buf.length, encrypted, at: Date.now() };
}

/**
 * Install Dexie write hooks on every shared table so ANY create/update/delete
 * of book data auto-marks the books dirty. This is the structural fix for the
 * recurring footgun where a new tile's save path (e.g. supplies' importOrders)
 * forgot to call markBooksDirty(), so the owner's edits never uploaded and
 * viewers never saw them. Idempotent. Safe on any device: markBooksDirty's
 * debounced upload self-skips for viewers, and writes made by loadSnapshot() are
 * suppressed via the _loading guard so a pull can't echo back into an upload.
 */
export function installDirtyHooks() {
  if (_hooksInstalled) return;
  _hooksInstalled = true;
  const onWrite = () => { if (!_loading) markBooksDirty(); };
  for (const name of sharedTables()) {
    try {
      const tbl = db.table(name);
      tbl.hook('creating', onWrite);
      tbl.hook('updating', onWrite);
      tbl.hook('deleting', onWrite);
    } catch { /* table absent on an older schema — skip */ }
  }
}

/** Mark the books changed; debounce an owner snapshot upload. Safe to spam. */
export function markBooksDirty() {
  _dirty = true;
  if (_timer) return;
  _timer = setTimeout(async () => {
    _timer = null;
    if (!_dirty || _inflight) return;
    _inflight = true;
    try { await shareSnapshotNow(); }
    catch (e) { console.warn('[shared-snapshot] upload failed', e?.message || e); }
    finally { _inflight = false; }
  }, DEBOUNCE_MS);
}

/** Fetch + parse the account's snapshot blob. Returns null when no blob exists.
 *  Throws code='ENCRYPTED_NEEDS_PHRASE' when the blob is encrypted and this
 *  device can't open it yet (new phone before recovery / any viewer). */
async function fetchSnapshot(acct) {
  const { url } = await presignSharedGet();
  const res = await fetch(url);
  if (res.status === 404 || res.status === 403) return null;
  if (!res.ok) throw new Error(`shared-snapshot: GET ${res.status}`);
  const buf = new Uint8Array(await res.arrayBuffer());
  if (gzMagic(buf)) return decodeSharedBlob(buf); // plaintext — no key needed
  // Encrypted: only the owner's own devices hold (or can recover) the key.
  let key = null;
  try { if (acct && acct.role === 'owner') key = (await deriveKeys(acct.sub, kv)).backupKey; }
  catch { /* fall through — decodeSharedBlob throws the typed error */ }
  return decodeSharedBlob(buf, { key, accountId: acct?.accountId || '' });
}

/** Clear + load the snapshot tables into Dexie, re-stamping userId to `mySub`. */
async function loadSnapshot(json, mySub) {
  const tables = json.tables || {};
  const targets = sharedTables().filter((t) => Array.isArray(tables[t]));
  _loading = true; // don't let the clear/bulkPut below trip the dirty hook into a re-upload
  try {
    await db.transaction('rw', targets.map((t) => db.table(t)), async () => {
      for (const t of targets) {
        await db.table(t).clear();
        const rows = tables[t];
        if (rows.length) {
          // Re-stamp ownership to THIS sub so existing where('userId') queries match.
          await db.table(t).bulkPut(rows.map((r) => (r && typeof r === 'object' && 'userId' in r ? { ...r, userId: mySub } : r)));
        }
      }
    });
  } finally {
    _loading = false;
  }
}

/** Pull + load the blob (read-only mirror / recovery). No-op when no blob. */
export async function importSharedSnapshot() {
  const acct = await currentAccount();
  if (!acct) return { skipped: true };
  const json = await fetchSnapshot(acct);
  if (!json) return { empty: true };
  await loadSnapshot(json, acct.sub);
  return { loaded: true, at: json.exportedAt };
}

/**
 * Initial, AWAITABLE rehydrate — call BEFORE rendering data views so the books
 * are present on first paint.
 *   - viewer: always mirror the owner's blob (read-only).
 *   - owner:  local-first. Pull from the cloud ONLY when local is empty — a fresh
 *             install / new phone (recovery). With existing local books we never
 *             load: the blob is downstream of local, so a pull must never be the
 *             thing that deletes the owner's data.
 */
export async function rehydrateIfNeeded() {
  const acct = await currentAccount();
  if (!acct) return { skipped: true };

  if (acct.role === 'viewer') {
    // A viewer can never open an encrypted blob (by design). Transient state:
    // the invite flow re-uploads plaintext; until then the viewer just waits.
    let json = null;
    try { json = await fetchSnapshot(acct); }
    catch (e) { if (e.code === 'ENCRYPTED_NEEDS_PHRASE') return { loaded: false, role: 'viewer', encrypted: true }; throw e; }
    if (json) await loadSnapshot(json, acct.sub);
    return { loaded: !!json, role: 'viewer' };
  }

  // Owner: recover from the cloud only on an empty device; otherwise keep local.
  if (await localBooksEmpty(acct.sub)) {
    let json = null;
    try { json = await fetchSnapshot(acct); }
    catch (e) {
      // Encrypted books + a fresh device (new salt): the boot path must ask for
      // the recovery phrase, install the original salt, and re-run this.
      if (e.code === 'ENCRYPTED_NEEDS_PHRASE') return { loaded: false, needsPhrase: true };
      throw e;
    }
    if (json) { await loadSnapshot(json, acct.sub); return { loaded: true, recovered: true }; }
    return { loaded: false };
  }
  return { loaded: false };
}

/**
 * Boot wiring for ONGOING sync (initial rehydrate is done by rehydrateIfNeeded(),
 * awaited at boot). The owner seeds the blob + uploads on change; viewers never
 * write.
 */
export function startSharedSync() {
  (async () => {
    const acct = await currentAccount();
    if (!acct) return;
    if (acct.role === 'viewer') return; // read-only; pulled at login + on foreground
    installDirtyHooks(); // owner: auto-mark dirty on any book write
    // Seed/refresh the blob for viewers + recovery (only when we have local books).
    try { if (!(await localBooksEmpty(acct.sub))) await shareSnapshotNow(); }
    catch (e) { console.warn('[shared-snapshot] owner seed failed', e?.message || e); }
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden' && _dirty) shareSnapshotNow().catch(() => {}); });
  })();
}
